import React, { useState, useEffect } from 'react';
import { Navbar, GalleryHeader, FooterOverlay } from '../../components';
import { galleryImages } from '../../constants';
import { StyleSheet, css } from 'aphrodite';

/* -------------------- COMPONENT -------------------- */

const GalleryPage = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeImages, setActiveImages] = useState([]);
  const [visible, setVisible] = useState(false);

  /* Fade-in on scroll */
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 150) setVisible(true);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Keyboard controls */
  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === null) return;

      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight')
        setLightboxIndex((prev) => (prev + 1) % activeImages.length);
      if (e.key === 'ArrowLeft')
        setLightboxIndex(
          (prev) => (prev - 1 + activeImages.length) % activeImages.length
        );
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, activeImages]);

  const openLightbox = (images, index) => {
    setActiveImages(images);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  return (
    <div className={css(styles.page)}>
      <Navbar />
      <GalleryHeader />

      {/* Savor The Flavors */}
      <section className={css(styles.section, visible && styles.fadeIn)}>
        <h3 className={css(styles.title)}>Savor The Flavors</h3>
        <div className={css(styles.line)} />

        <div className={css(styles.grid)}>
          {galleryImages.savorTheFlavors.map((img, i) => (
            <div
              key={i}
              className={css(styles.card)}
              onClick={() => openLightbox(galleryImages.savorTheFlavors, i)}
            >
              <img src={img} alt="" className={css(styles.image)} />
            </div>
          ))}
        </div>
      </section>

      {/* Discover Our Space */}
      <section className={css(styles.section, visible && styles.fadeIn)}>
        <h3 className={css(styles.title)}>Discover Our Space</h3>
        <div className={css(styles.line)} />

        <div className={css(styles.grid)}>
          {galleryImages.discoverOurSpace.map((img, i) => (
            <div
              key={i}
              className={css(styles.card)}
              onClick={() => openLightbox(galleryImages.discoverOurSpace, i)}
            >
              <img src={img} alt="" className={css(styles.image)} />
            </div>
          ))}
        </div>
      </section>

      <FooterOverlay />

      {/* LIGHTBOX */}
      {lightboxIndex !== null && (
        <div className={css(styles.lightbox)} onClick={closeLightbox}>
          {/* CLOSE BUTTON */}
          <button
            className={css(styles.closeButton)}
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            aria-label="Close image"
          >
            ×
          </button>

          <img
            src={activeImages[lightboxIndex]}
            alt=""
            className={css(styles.lightboxImage)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GalleryPage;

/* -------------------- STYLES -------------------- */

const styles = StyleSheet.create({
  page: {
    backgroundColor: 'var(--color-cream)',
    width: '100%',
  },

  section: {
    padding: '5rem 2rem',
    textAlign: 'center',
    opacity: 0,
    transform: 'translateY(40px)',
    transition: 'all 0.9s ease',
  },

  fadeIn: {
    opacity: 1,
    transform: 'translateY(0)',
  },

  title: {
    fontFamily: 'var(--font-base)',
    color: 'var(--color-green)',
    fontSize: 'clamp(2.2rem,4vw,4rem)',
    fontWeight: 500,
    marginTop: '-2rem',
    marginBottom: '1rem',
  },

  line: {
    height: '1px',
    width: '80px',
    backgroundColor: 'rgba(35,79,64,.3)',
    margin: '0 auto 3rem',
  },

  grid: {
    maxWidth: '1300px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '1rem',
  },

  card: {
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: '0 18px 45px rgba(23,60,50,.1)',
    transition: 'transform 0.4s ease',
    ':hover': {
      transform: 'scale(1.04)',
    },
  },

  image: {
    width: '100%',
    height: '320px',
    objectFit: 'cover',
  },

  /* Lightbox */
  lightbox: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },

  lightboxImage: {
    maxWidth: '90%',
    maxHeight: '85%',
    borderRadius: '18px',
    boxShadow: '0 30px 80px rgba(0,0,0,0.9)',
  },

  closeButton: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    fontSize: '36px',
    color: '#fff',
    background: 'rgba(0,0,0,0.6)',
    border: 'none',
    borderRadius: '50%',
    width: '44px',
    height: '44px',
    cursor: 'pointer',
    zIndex: 10000,
    lineHeight: '44px',
    textAlign: 'center',
    ':hover': {
      background: 'rgba(255,255,255,0.2)',
    },
  },
});
